import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  FileText, Plus, Eye, Download, RefreshCw, CheckCircle, 
  Clock, Send, XCircle, DollarSign, Calendar, Building2, Printer
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const CHECK_TYPE_LABELS = {
  id_check: 'ID Verification',
  criminal_court_check: 'Criminal Court Check',
  court_advocate_check: 'Court Advocate Check',
  address_check: 'Address Verification',
  employment_check: 'Employment Verification',
  education_check: 'Education Verification',
  reference_check: 'Reference Check',
  credit_check: 'Credit Check',
  drug_test: 'Drug Test',
  world_criminal_db: 'World Database Check',
  india_criminal_db: 'India Criminal Database'
};

export default function VendorInvoices({ vendors = [] }) {
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(null);
  const [preview, setPreview] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  const [generateForm, setGenerateForm] = useState({
    vendor_id: '',
    period_from: '',
    period_to: '',
    gst_rate: 18,
    notes: ''
  });

  const [paymentForm, setPaymentForm] = useState({
    payment_reference: ''
  });

  useEffect(() => {
    fetchInvoices();
  }, [statusFilter]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const res = await axios.get(`/vendor-invoices${params}`);
      setInvoices(res.data || []);
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = async () => {
    if (!generateForm.vendor_id || !generateForm.period_from || !generateForm.period_to) {
      toast.error('Please select vendor and date range');
      return;
    }

    try {
      const res = await axios.get(
        `/vendor-invoices/preview/${generateForm.vendor_id}?period_from=${generateForm.period_from}&period_to=${generateForm.period_to}`
      );
      setPreview(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to preview invoice');
    }
  };

  const handleGenerate = async () => {
    if (!preview || preview.eligible_assignments === 0) {
      toast.error('No eligible assignments to invoice');
      return;
    }

    setGenerating(true);
    try {
      const res = await axios.post('/vendor-invoices/generate', generateForm);
      toast.success(`Invoice ${res.data.invoice_number} generated successfully`);
      setShowGenerateDialog(false);
      setPreview(null);
      setGenerateForm({
        vendor_id: '',
        period_from: '',
        period_to: '',
        gst_rate: 18,
        notes: ''
      });
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate invoice');
    } finally {
      setGenerating(false);
    }
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    try {
      await axios.put(`/vendor-invoices/${invoiceId}/status`, { 
        status: newStatus,
        payment_reference: paymentForm.payment_reference
      });
      toast.success(`Invoice marked as ${newStatus}`);
      setShowPaymentDialog(null);
      setPaymentForm({ payment_reference: '' });
      fetchInvoices();
      if (showInvoiceDetail?.id === invoiceId) {
        const res = await axios.get(`/vendor-invoices/${invoiceId}`);
        setShowInvoiceDetail(res.data);
      }
    } catch (error) {
      toast.error('Failed to update invoice status');
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!confirm('Are you sure you want to delete this draft invoice?')) return;
    
    try {
      await axios.delete(`/vendor-invoices/${invoiceId}`);
      toast.success('Invoice deleted');
      fetchInvoices();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete invoice');
    }
  };

  const generateInvoicePDF = (invoice) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    
    // Header
    doc.setFillColor(30, 41, 59);
    doc.rect(0, 0, pageWidth, 45, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(24);
    doc.setFont('helvetica', 'bold');
    doc.text('JustVerified', 14, 20);
    
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Background Verification Services', 14, 28);
    doc.text('GST: XXXXXXXXXXXXXXX', 14, 35);
    
    // Invoice badge
    doc.setFillColor(16, 185, 129);
    doc.roundedRect(pageWidth - 60, 10, 46, 16, 2, 2, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.text('INVOICE', pageWidth - 37, 20, { align: 'center' });
    
    // Invoice details
    doc.setTextColor(30, 41, 59);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text(`Invoice #: ${invoice.invoice_number}`, pageWidth - 14, 55, { align: 'right' });
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Date: ${invoice.invoice_date}`, pageWidth - 14, 62, { align: 'right' });
    doc.text(`Period: ${invoice.period_from} to ${invoice.period_to}`, pageWidth - 14, 69, { align: 'right' });
    
    // Bill To
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Bill To:', 14, 55);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(invoice.vendor_name || 'N/A', 14, 62);
    if (invoice.vendor_address) doc.text(invoice.vendor_address, 14, 69);
    if (invoice.vendor_gst) doc.text(`GST: ${invoice.vendor_gst}`, 14, 76);
    if (invoice.vendor_pan) doc.text(`PAN: ${invoice.vendor_pan}`, 14, 83);
    
    // Line items table
    const tableData = invoice.line_items.map((item, idx) => [
      idx + 1,
      CHECK_TYPE_LABELS[item.check_type] || item.check_type,
      item.employee_name || '-',
      item.client_name || '-',
      item.location || '-',
      `₹${item.price.toFixed(2)}`
    ]);
    
    autoTable(doc, {
      startY: 95,
      head: [['#', 'Check Type', 'Employee', 'Client', 'Location', 'Amount']],
      body: tableData,
      theme: 'striped',
      headStyles: { fillColor: [30, 41, 59], textColor: [255, 255, 255], fontSize: 9 },
      bodyStyles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 10 },
        5: { halign: 'right' }
      },
      margin: { left: 14, right: 14 }
    });
    
    const finalY = doc.lastAutoTable.finalY + 10;
    
    // Summary
    const summaryX = pageWidth - 80;
    doc.setFontSize(10);
    doc.text('Subtotal:', summaryX, finalY);
    doc.text(`₹${invoice.subtotal.toFixed(2)}`, pageWidth - 14, finalY, { align: 'right' });
    
    doc.text(`GST (${invoice.gst_rate}%):`, summaryX, finalY + 8);
    doc.text(`₹${invoice.gst_amount.toFixed(2)}`, pageWidth - 14, finalY + 8, { align: 'right' });
    
    doc.setDrawColor(200, 200, 200);
    doc.line(summaryX, finalY + 12, pageWidth - 14, finalY + 12);
    
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Total:', summaryX, finalY + 20);
    doc.text(`₹${invoice.total_amount.toFixed(2)}`, pageWidth - 14, finalY + 20, { align: 'right' });
    
    // Notes
    if (invoice.notes) {
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text('Notes:', 14, finalY + 35);
      doc.text(invoice.notes, 14, finalY + 42);
    }
    
    // Footer
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text('Generated by JustVerified CRM', 14, doc.internal.pageSize.getHeight() - 10);
    doc.text(`Status: ${invoice.status.toUpperCase()}`, pageWidth - 14, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
    
    doc.save(`${invoice.invoice_number}.pdf`);
    toast.success('Invoice PDF downloaded');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'draft':
        return <Badge className="bg-slate-100 text-slate-700">Draft</Badge>;
      case 'sent':
        return <Badge className="bg-blue-100 text-blue-700">Sent</Badge>;
      case 'paid':
        return <Badge className="bg-emerald-100 text-emerald-700">Paid</Badge>;
      case 'cancelled':
        return <Badge className="bg-rose-100 text-rose-700">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Calculate stats
  const totalInvoices = invoices.length;
  const draftCount = invoices.filter(i => i.status === 'draft').length;
  const sentCount = invoices.filter(i => i.status === 'sent').length;
  const paidCount = invoices.filter(i => i.status === 'paid').length;
  const totalPaid = invoices.filter(i => i.status === 'paid').reduce((sum, i) => sum + i.total_amount, 0);
  const totalPending = invoices.filter(i => i.status === 'sent').reduce((sum, i) => sum + i.total_amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="vendor-invoices">
      {/* Stats */}
      <div className="grid grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-900">{totalInvoices}</div>
            <p className="text-sm text-slate-500">Total Invoices</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-slate-600">{draftCount}</div>
            <p className="text-sm text-slate-500">Drafts</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-blue-600">{sentCount}</div>
            <p className="text-sm text-slate-500">Sent</p>
          </CardContent>
        </Card>
        <Card className="border-emerald-200">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-emerald-600">{paidCount}</div>
            <p className="text-sm text-slate-500">Paid</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200">
          <CardContent className="pt-4">
            <div className="text-2xl font-bold text-amber-600">₹{totalPending.toLocaleString()}</div>
            <p className="text-sm text-slate-500">Pending Payment</p>
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Invoices</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="sent">Sent</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchInvoices}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        <Button onClick={() => setShowGenerateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="generate-invoice-btn">
          <Plus className="w-4 h-4 mr-2" />
          Generate Invoice
        </Button>
      </div>

      {/* Invoices Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Invoice #</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Items</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                    No invoices found. Click "Generate Invoice" to create one.
                  </TableCell>
                </TableRow>
              ) : (
                invoices.map((invoice) => (
                  <TableRow key={invoice.id}>
                    <TableCell className="font-mono font-medium">{invoice.invoice_number}</TableCell>
                    <TableCell>{invoice.vendor_name}</TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {invoice.period_from} to {invoice.period_to}
                    </TableCell>
                    <TableCell>{invoice.total_assignments || invoice.line_items?.length || 0}</TableCell>
                    <TableCell className="text-right font-medium">₹{invoice.total_amount?.toLocaleString()}</TableCell>
                    <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                    <TableCell className="text-sm text-slate-600">
                      {new Date(invoice.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setShowInvoiceDetail(invoice)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => generateInvoicePDF(invoice)}>
                          <Download className="w-4 h-4" />
                        </Button>
                        {invoice.status === 'draft' && (
                          <>
                            <Button variant="ghost" size="sm" className="text-blue-600" onClick={() => handleUpdateStatus(invoice.id, 'sent')}>
                              <Send className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => handleDelete(invoice.id)}>
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        {invoice.status === 'sent' && (
                          <Button variant="ghost" size="sm" className="text-emerald-600" onClick={() => setShowPaymentDialog(invoice)}>
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Generate Invoice Dialog */}
      <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Generate Vendor Invoice
            </DialogTitle>
            <DialogDescription>
              Create an invoice for completed vendor assignments
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Vendor *</Label>
                <Select 
                  value={generateForm.vendor_id} 
                  onValueChange={(v) => {
                    setGenerateForm(prev => ({ ...prev, vendor_id: v }));
                    setPreview(null);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Period From *</Label>
                <Input 
                  type="date"
                  value={generateForm.period_from}
                  onChange={(e) => {
                    setGenerateForm(prev => ({ ...prev, period_from: e.target.value }));
                    setPreview(null);
                  }}
                />
              </div>
              <div>
                <Label>Period To *</Label>
                <Input 
                  type="date"
                  value={generateForm.period_to}
                  onChange={(e) => {
                    setGenerateForm(prev => ({ ...prev, period_to: e.target.value }));
                    setPreview(null);
                  }}
                />
              </div>
              <div>
                <Label>GST Rate (%)</Label>
                <Input 
                  type="number"
                  value={generateForm.gst_rate}
                  onChange={(e) => setGenerateForm(prev => ({ ...prev, gst_rate: parseFloat(e.target.value) || 18 }))}
                />
              </div>
              <div>
                <Button onClick={handlePreview} className="mt-6 w-full" variant="outline">
                  <Eye className="w-4 h-4 mr-2" />
                  Preview Invoice
                </Button>
              </div>
            </div>

            <div>
              <Label>Notes (Optional)</Label>
              <Textarea 
                value={generateForm.notes}
                onChange={(e) => setGenerateForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes for the invoice..."
                rows={2}
              />
            </div>

            {preview && (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    Invoice Preview
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-slate-900">{preview.eligible_assignments}</p>
                      <p className="text-sm text-slate-500">Assignments</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-slate-900">₹{preview.subtotal?.toLocaleString()}</p>
                      <p className="text-sm text-slate-500">Subtotal</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-emerald-600">₹{preview.total_amount?.toLocaleString()}</p>
                      <p className="text-sm text-slate-500">Total (incl. GST)</p>
                    </div>
                  </div>
                  
                  {preview.eligible_assignments > 0 && (
                    <div className="max-h-48 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Check Type</TableHead>
                            <TableHead>Employee</TableHead>
                            <TableHead className="text-right">Price</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {preview.assignments?.slice(0, 10).map((a, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="text-sm">{CHECK_TYPE_LABELS[a.check_type] || a.check_type}</TableCell>
                              <TableCell className="text-sm">{a.employee_name || '-'}</TableCell>
                              <TableCell className="text-sm text-right">₹{a.price}</TableCell>
                            </TableRow>
                          ))}
                          {preview.assignments?.length > 10 && (
                            <TableRow>
                              <TableCell colSpan={3} className="text-center text-sm text-slate-500">
                                +{preview.assignments.length - 10} more items
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowGenerateDialog(false); setPreview(null); }}>
              Cancel
            </Button>
            <Button 
              onClick={handleGenerate} 
              disabled={!preview || preview.eligible_assignments === 0 || generating}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {generating ? 'Generating...' : 'Generate Invoice'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Detail Dialog */}
      <Dialog open={!!showInvoiceDetail} onOpenChange={() => setShowInvoiceDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Invoice {showInvoiceDetail?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {showInvoiceDetail && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{showInvoiceDetail.vendor_name}</p>
                  <p className="text-sm text-slate-500">{showInvoiceDetail.vendor_email}</p>
                </div>
                {getStatusBadge(showInvoiceDetail.status)}
              </div>

              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Invoice Date</p>
                  <p className="font-medium">{showInvoiceDetail.invoice_date}</p>
                </div>
                <div>
                  <p className="text-slate-500">Period</p>
                  <p className="font-medium">{showInvoiceDetail.period_from} to {showInvoiceDetail.period_to}</p>
                </div>
                <div>
                  <p className="text-slate-500">Total Items</p>
                  <p className="font-medium">{showInvoiceDetail.line_items?.length || 0}</p>
                </div>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>#</TableHead>
                    <TableHead>Check Type</TableHead>
                    <TableHead>Employee</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {showInvoiceDetail.line_items?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{CHECK_TYPE_LABELS[item.check_type] || item.check_type}</TableCell>
                      <TableCell>{item.employee_name || '-'}</TableCell>
                      <TableCell>{item.client_name || '-'}</TableCell>
                      <TableCell className="text-right">₹{item.price?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Subtotal</span>
                    <span>₹{showInvoiceDetail.subtotal?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">GST ({showInvoiceDetail.gst_rate}%)</span>
                    <span>₹{showInvoiceDetail.gst_amount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total</span>
                    <span className="text-emerald-600">₹{showInvoiceDetail.total_amount?.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {showInvoiceDetail.notes && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-slate-600">Notes:</p>
                  <p className="text-sm">{showInvoiceDetail.notes}</p>
                </div>
              )}

              {showInvoiceDetail.status === 'paid' && (
                <div className="bg-emerald-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-emerald-600">Payment Details:</p>
                  <p className="text-sm">Paid on: {new Date(showInvoiceDetail.paid_at).toLocaleDateString()}</p>
                  {showInvoiceDetail.payment_reference && (
                    <p className="text-sm">Reference: {showInvoiceDetail.payment_reference}</p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInvoiceDetail(null)}>Close</Button>
            <Button onClick={() => generateInvoicePDF(showInvoiceDetail)} className="bg-slate-900">
              <Download className="w-4 h-4 mr-2" />
              Download PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={!!showPaymentDialog} onOpenChange={() => setShowPaymentDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              Mark as Paid
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-slate-600">
              Mark invoice <strong>{showPaymentDialog?.invoice_number}</strong> as paid?
            </p>
            <p className="text-lg font-bold text-emerald-600">
              Amount: ₹{showPaymentDialog?.total_amount?.toLocaleString()}
            </p>
            <div>
              <Label>Payment Reference (Optional)</Label>
              <Input 
                value={paymentForm.payment_reference}
                onChange={(e) => setPaymentForm({ payment_reference: e.target.value })}
                placeholder="Transaction ID, cheque number, etc."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(null)}>Cancel</Button>
            <Button onClick={() => handleUpdateStatus(showPaymentDialog.id, 'paid')} className="bg-emerald-600 hover:bg-emerald-700">
              Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
